import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  formatBlogDate,
  getBlogContentLanguage,
  getBlogLabels,
  isPrimaryBlogLanguage,
} from "../blogContent";
import { usePublicBlogPosts } from "../usePublicBlogPosts";
import NotFoundPage from "./NotFoundPage";
import styles from "./BlogPage.module.css";

const BlogPostPage: React.FC = () => {
  const { lang = "en", slug } = useParams<{ lang: string; slug: string }>();
  const { posts, loading } = usePublicBlogPosts();
  const post = posts.find((item) => item.slug === slug);
  const contentLanguage = getBlogContentLanguage(lang);
  const labels = getBlogLabels(lang);
  if (!post && loading) {
    return (
      <div className={styles.page} role="status">
        <p className={styles.empty}>{labels.loading}</p>
      </div>
    );
  }
  if (!post) {
    return <NotFoundPage lang={lang} />;
  }

  const translation = post.translations[contentLanguage];
  const externalSources = post.externalSources || [];

  return (
    <div className={styles.page} lang={contentLanguage}>
      <article className={styles.article}>
        <header className={styles.articleHeader}>
          <Link className={styles.backLink} to={`/${lang}/blog`}>
            ← {labels.back}
          </Link>
          {!isPrimaryBlogLanguage(lang) && (
            <p className={styles.fallbackNotice}>{labels.fallback}</p>
          )}
          <p className={styles.eyebrow}>AUTOVID · GUIDE</p>
          <h1 className={styles.title}>{translation.title}</h1>
          <p className={styles.meta}>
            <time dateTime={post.date}>{formatBlogDate(post.date, contentLanguage)}</time>
            {` · ${post.author}`}
          </p>
        </header>

        <div className={styles.articleBody}>
          {post.image && (
            <img
              className={styles.articleImage}
              src={post.image.path}
              alt={post.image.alt[contentLanguage]}
            />
          )}
          {translation.intro.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}

          {translation.sections.map((section) => (
            <section key={section.heading}>
              <h2>{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}

          {translation.faq.length > 0 && (
            <section className={styles.faq} aria-labelledby="blog-faq-title">
              <h2 id="blog-faq-title">{labels.faq}</h2>
              {translation.faq.map((item) => (
                <details key={item.question}>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </section>
          )}

          {(post.sources.length > 0 || externalSources.length > 0) && (
            <section className={styles.sources} aria-labelledby="blog-sources-title">
              <h2 id="blog-sources-title">{labels.sources}</h2>
              <ul>
                {post.sources.map((source) => (
                  <li key={source.path}>
                    <Link to={`/${lang}${source.path}`}>{source.label}</Link>
                  </li>
                ))}
                {externalSources.map((source) => (
                  <li key={source.url}>
                    <a href={source.url} rel="noopener noreferrer" target="_blank">
                      {source.title}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {post.internalPath && translation.ctaLabel && (
            <Link className={styles.cta} to={`/${lang}${post.internalPath}`}>
              {translation.ctaLabel}
            </Link>
          )}
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
